import { useState, useCallback, useRef } from 'react'

export interface ConfirmOptions {
  title?: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'destructive'
}

interface ConfirmState extends ConfirmOptions {
  open: boolean
  onConfirm: () => void | Promise<void>
  onCancel?: () => void
  isLoading?: boolean
}

export function useConfirm() {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)

  const confirm = useCallback(
    (
      options: ConfirmOptions,
      onConfirm: () => void | Promise<void>
    ): Promise<boolean> => {
      return new Promise((resolve) => {
        resolverRef.current = resolve
        setConfirmState({
          ...options,
          open: true,
          onConfirm: async () => {
            try {
              await onConfirm()
              resolverRef.current?.(true)
              resolverRef.current = null
            } catch (error) {
              // Resolve to false if onConfirm throws
              resolverRef.current?.(false)
              resolverRef.current = null
              throw error
            } finally {
              setConfirmState(null)
            }
          },
          onCancel: () => {
            resolverRef.current?.(false)
            resolverRef.current = null
            setConfirmState(null)
          },
          isLoading: false,
        })
      })
    },
    []
  )

  const close = useCallback(() => {
    if (resolverRef.current) {
      resolverRef.current(false)
      resolverRef.current = null
    }
    setConfirmState(null)
  }, [])

  const setLoading = useCallback((isLoading: boolean) => {
    setConfirmState((prev) => (prev ? { ...prev, isLoading } : null))
  }, [])

  return {
    confirm,
    close,
    setLoading,
    confirmState,
  }
}
